/**
 * Basic Auth helpers for Authenticated User mutating actions.
 */

const USER_KEY = 'basic_auth_username';
const PASS_KEY = 'basic_auth_password';

export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  return 'http://localhost:8080/api';
};

export const storeBasicAuth = (username, password) => {
  localStorage.setItem(USER_KEY, username);
  localStorage.setItem(PASS_KEY, password);
};

export const clearBasicAuth = () => {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(PASS_KEY);
};

export const getBasicAuthCredentials = () => {
  const username = localStorage.getItem(USER_KEY) || '';
  const password = localStorage.getItem(PASS_KEY) || '';
  return { username, password };
};

export const hasBasicAuth = () => {
  const { username, password } = getBasicAuthCredentials();
  return Boolean(username && password);
};

export const basicAuthHeaderValue = () => {
  const { username, password } = getBasicAuthCredentials();
  if (!username || !password) return null;
  return `Basic ${btoa(`${username}:${password}`)}`;
};

export const authHeaders = (extra = {}) => {
  const headers = { ...extra };
  const auth = basicAuthHeaderValue();
  if (auth) {
    headers.Authorization = auth;
  }
  return headers;
};

export const createApiAxios = async () => {
  const axios = (await import('axios')).default;
  const headers = authHeaders({ 'Content-Type': 'application/json' });
  return axios.create({
    baseURL: getApiBaseUrl(),
    headers,
  });
};

/** Clear stored credentials after a failed auth probe or 401 on a privileged action. */
export const handleUnauthorized = () => {
  clearBasicAuth();
};

/**
 * Probe GET /auth/check with stored Basic Auth.
 * Returns true for Authenticated User; on 401 clears credentials and returns false.
 * Network / non-401 failures return false without clearing (caller may clear on Login).
 */
export const probeAuth = async () => {
  if (!hasBasicAuth()) {
    return false;
  }
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/check`, {
      method: 'GET',
      headers: authHeaders(),
    });
    if (response.status === 401) {
      handleUnauthorized();
      return false;
    }
    return response.ok;
  } catch {
    return false;
  }
};
